import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from '../components/MDTypography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { db, collection, query, getDocs, where } from '../firebaseConfig';
import { usePeriod } from '../components/PeriodContext';

const TeamWinRateGraph = ({ userID, tradeAccountID }) => {
  const { selectedPeriod, selectedYear, selectedMonth, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(selectedYear || new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradesSnapshot = await getDocs(tradesQuery);
        const tradesData = tradesSnapshot.docs.map((doc) => ({
          id: doc.id,
          accountID: tradeAccountID,
          date: doc.data().date ? new Date(doc.data().date) : new Date(),
          ...doc.data(),
        }));
        console.log('TeamWinRateGraph: Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, tradesData);
        setTrades(tradesData);
      } catch (error) {
        console.error('TeamWinRateGraph: Error fetching trades:', error);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };

    if (userID && tradeAccountID) {
      fetchTrades();
    } else {
      setTrades([]);
      setLoading(false);
    }
  }, [userID, tradeAccountID]);

  const filterTrades = () => {
    return trades.filter((trade) => {
      const tradeDate = new Date(trade.date);
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return (
            tradeDate.getWeek() === new Date().getWeek() &&
            tradeDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-month') {
          return (
            tradeDate.getMonth() === new Date().getMonth() &&
            tradeDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else {
          return true; // all-time
        }
      };

      const matchesTag =
        selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const calculateWinRate = (trades, year) => {
    const monthlyWins = Array(12).fill(0);
    const monthlyTrades = Array(12).fill(0);

    trades.forEach((trade) => {
      const tradeDate = new Date(trade.date);
      if (tradeDate.getFullYear() === year && trade.status === 'Closed') {
        const month = tradeDate.getMonth();
        monthlyTrades[month]++;
        if (parseFloat(trade.returns) > 0) {
          monthlyWins[month]++;
        }
      }
    });

    return monthlyTrades.map((totalTrades, index) => {
      return totalTrades > 0 ? (monthlyWins[index] / totalTrades) * 100 : 0;
    });
  };

  const filteredTrades = filterTrades();
  const winRates = calculateWinRate(filteredTrades, year);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Win Rate (%)',
        data: winRates,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const tooltipContent = `The Win Rate Analysis shows the percentage of winning trades per month for the user. Hover over any data point to view detailed win rate stats for that month. Use the year selector to compare performance across different years and identify the strongest trading months.`;

  if (loading) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Win Rate
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2023, 2024, 2025, 2026].map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamWinRateGraph.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
};

export default TeamWinRateGraph;